import type { Conversation, ConversationClient } from '@/api/interfaces/texting.types';
import type { TwilioPhoneType } from '@/api/interfaces/twilio';
import { formatPhoneNumber } from '@/shared/utils/phone-number';
import type { Nullable, PickArray } from '@/shared/utils/type-helpers';

type FileAddressAndRef = PickArray<Conversation['files'], 'address' | 'ref_number'>;
type PartialNullableConversationClient = Partial<
    Nullable<Pick<ConversationClient, 'first_name' | 'last_name' | 'email' | 'phone_number'>>
>;

export const MAX_SHOWN_SMS_FILES = 2;

export const getClientLabel = ({ first_name, last_name, email, phone_number }: PartialNullableConversationClient): string => {
    if (first_name || last_name) {
        return `${first_name ?? ''} ${last_name ?? ''}`.trim();
    }
    return email || formatPhoneNumber(phone_number ?? '') || '??';
};

export const fileToString = ({ address, ref_number }: FileAddressAndRef[number]) =>
    address && ref_number ? `${address} • ${ref_number}` : address || ref_number;

export const listClientNames = (clients: ConversationClient[]): string => clients.map(getClientLabel).join(', ');

export const appendSignature = (message: string, conversation: Conversation, firstName?: string): string => {
    const titleCo = conversation.branch.title_company ?? '';
    const EN_DASH = '–'; // not a hyphen
    const signature = [firstName, titleCo].filter(Boolean).join(' @ ');
    return signature ? `${message}\n\n${EN_DASH} ${signature}` : message;
};

export const isConversationStarted = ({ id }: Conversation) => id !== null;

const getClientIdsKey = ({ clients }: Conversation): string =>
    clients
        .map(({ id }) => id)
        .sort()
        .join();

export const isSameConversation = (incoming: Conversation, current?: Conversation): boolean =>
    isConversationStarted(incoming) && incoming.id === current?.id && getClientIdsKey(incoming) === getClientIdsKey(current);

export const isTextablePhoneType = (phoneType: TwilioPhoneType | null): boolean => phoneType === 'mobile';

/** Creates a stable lookup key for in-memory Conversation message drafts */
export const getDraftKey = (convo: Conversation): string =>
    [...convo.clients, convo.branch]
        .map(item => item?.phone_number)
        .filter((item): item is string => Boolean(item))
        .sort()
        .join();
